/* eslint-disable no-console */
import { CHATKIT_CONFIGS } from './configs';
import { extractContextFromDataset } from './helpers';

const CHATKIT_SCRIPT_ID = 'openai-chatkit-script';
const CHATKIT_SCRIPT_URL = 'https://cdn.platform.openai.com/deployments/chatkit/chatkit.js';

const loadChatkitScript = () => {
  if (document.getElementById(CHATKIT_SCRIPT_ID)) return Promise.resolve();

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = CHATKIT_SCRIPT_URL;
    script.async = true;
    script.id = CHATKIT_SCRIPT_ID;
    script.onload = resolve;
    document.head.appendChild(script);
  });
};

window.ChatKitWidget = {
  widgets: new Map(),

  async fetchToken(context = {}) {
    try {
      const body = Object.keys(context).length > 0 ? { context } : {};
      const res = await fetch('/ai/chatkit_tokens', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      return { token: data.token, flowType: data.flow_type };
    } catch (error) {
      console.error('Error fetching ChatKit token:', error);
      return null;
    }
  },

  getConfig(container) {
    const type = container.dataset.chatkitType || this.flowType;
    const config = { ...CHATKIT_CONFIGS[type] };

    const dynamicContext = extractContextFromDataset(container);
    if (Object.keys(dynamicContext).length > 0) {
      config.context = { ...config.context, ...dynamicContext };
    }

    return config;
  },

  async prepareConfigAndToken(container) {
    if (container.dataset.chatkitType) {
      const config = this.getConfig(container);
      const tokenData = await this.fetchToken(config.context);
      return { config, tokenData };
    }

    const tokenData = await this.fetchToken({});
    if (!tokenData) return null;

    this.flowType = tokenData.flowType;
    const config = this.getConfig(container);
    return { config, tokenData };
  },

  async initializeWidget(container) {
    await loadChatkitScript();

    const kit = container.querySelector('openai-chatkit');
    if (!kit) return;

    const widgetId = container.id;
    if (this.widgets.has(widgetId) && this.widgets.get(widgetId).isInitialized) {
      return;
    }

    if (kit.shadowRoot && kit.shadowRoot.querySelector('iframe')) {
      this.widgets.set(widgetId, { isInitialized: true });
      return;
    }

    const result = await this.prepareConfigAndToken(container);
    const { config, tokenData } = result;

    try {
      const userSettings = document.querySelector('[data-controller="user-account-settings"]');
      const appearanceMode = userSettings?.dataset?.userAccountSettingsThemeValue || 'light';

      kit.setOptions({
        api: {
          async getClientSecret(existing) {
            if (existing) {
              const newTokenData = await window.ChatKitWidget.fetchToken(config.context);
              if (newTokenData) return newTokenData.token;
              throw new Error('Failed to refresh ChatKit token');
            }
            return tokenData.token;
          }
        },
        theme: {
          colorScheme: appearanceMode
        },
        composer: {
          placeholder: config.placeholder,
          attachments: config.attachments
        },
        startScreen: {
          greeting: config.greeting,
          prompts: config.prompts
        }
      });

      this.widgets.set(widgetId, { isInitialized: true, config });
    } catch (error) {
      console.error('Error initializing ChatKit:', error);
    }
  },

  async toggleWidget() {
    const container = document.getElementById('chatkit-widget-container');
    if (!container) return;

    const win = container.querySelector('#chatkit-window');
    const toggle = container.querySelector('#chatkit-toggle');
    if (!win || !toggle) return;

    const isOpen = win.style.display === 'block';

    if (!isOpen) {
      await this.initializeWidget(container);
      win.style.display = 'block';
      toggle.style.display = 'none';
    } else {
      win.style.display = 'none';
      toggle.style.display = 'block';
    }
  },

  setupToggleWidget() {
    const container = document.getElementById('chatkit-widget-container');
    if (!container) return;

    const toggle = container.querySelector('#chatkit-toggle');
    const close = container.querySelector('#chatkit-close');

    this.boundToggleHandler = () => this.toggleWidget();

    if (toggle) {
      toggle.addEventListener('click', this.boundToggleHandler);
    }

    if (close) {
      close.addEventListener('click', this.boundToggleHandler);
    }
  },

  destroyWidget(widgetId) {
    const container = document.getElementById(widgetId);
    if (!container) return;

    const kit = container.querySelector('openai-chatkit');
    if (kit && kit.shadowRoot) {
      kit.shadowRoot.innerHTML = '';
    }

    if (widgetId === 'chatkit-widget-container' && this.boundToggleHandler) {
      const toggle = container.querySelector('#chatkit-toggle');
      const close = container.querySelector('#chatkit-close');
      if (toggle) toggle.removeEventListener('click', this.boundToggleHandler);
      if (close) close.removeEventListener('click', this.boundToggleHandler);
    }

    this.widgets.delete(widgetId);
  },

  destroyAll() {
    this.widgets.forEach((_, widgetId) => {
      this.destroyWidget(widgetId);
    });
  }
};

// Cleanup on turbo cache
document.addEventListener('turbo:before-cache', () => {
  window.ChatKitWidget.destroyAll();
});
